import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Mic2, Mail, Lock, User, Chrome, Eye, EyeOff, Info } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import MonetizationPlanModal from '../components/MonetizationPlanModal';
import StripeSetupWizardModal from '../components/StripeSetupWizardModal';
import { trackEvent } from '../utils/analytics';

const Signup = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [searchParams] = useSearchParams();
  
  // PART A: Only allow reviewer signup - artists must use public submission or waitlist
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    userType: 'reviewer' // Fixed to reviewer only
  });
  
  // PART B: Show monetization modal immediately after signup
  const [showMonetizationModal, setShowMonetizationModal] = useState(false);
  const [showStripeWizard, setShowStripeWizard] = useState(false);
  const [newUser, setNewUser] = useState(null);

  const [usernameStatus, setUsernameStatus] = useState({
    checking: false,
    available: null,
    suggestions: []
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const checkUsername = async (username) => {
    if (!username || username.length < 3) return;
    
    setUsernameStatus({ checking: true, available: null, suggestions: [] });
    
    try {
      const response = await fetch(`/api/auth/check-username/${username}`);
      const data = await response.json();
      setUsernameStatus({
        checking: false,
        available: data.available,
        suggestions: data.suggestions
      });
    } catch (error) {
      console.error('Username check error:', error);
      setUsernameStatus({ checking: false, available: null, suggestions: [] });
    }
  };

  const handleUsernameChange = (e) => {
    const username = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setFormData({ ...formData, username });
    
    // Debounce username check
    clearTimeout(window.usernameCheckTimeout);
    window.usernameCheckTimeout = setTimeout(() => checkUsername(username), 500);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // GA4: Track signup start
    trackEvent('reviewer_signup_start');
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Passwords don&apos;t match",
        description: "Please make sure your passwords match.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.username || formData.username.length < 3) {
      toast({
        title: "Username required",
        description: "Please choose a username (at least 3 characters).",
        variant: "destructive"
      });
      return;
    }

    if (usernameStatus.available === false) {
      toast({
        title: "Username taken",
        description: "Please choose a different username.",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          username: formData.username,
          password: formData.password,
          user_type: formData.userType
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Store token
        localStorage.setItem('access_token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // GA4: Track signup complete with user intent
        trackEvent('reviewer_signup_complete', {
          user_intent: sessionStorage.getItem('mcm_user_intent') || 'direct'
        });
        
        // Meta Pixel: Track CompleteRegistration for Facebook Ads conversion tracking
        // This fires ONLY after successful backend account creation (response.ok = true)
        try {
          if (window.fbq) {
            window.fbq('track', 'CompleteRegistration', {
              content_name: 'Reviewer Signup',
              status: 'completed',
              currency: 'USD',
              value: 0
            });
            console.log('✅ Meta Pixel: CompleteRegistration event fired');
          } else {
            console.warn('⚠️ Meta Pixel: fbq not available');
          }
        } catch (fbErr) {
          console.error('❌ Meta Pixel error:', fbErr);
        }
        
        toast({ 
          title: "Account created!", 
          description: "Welcome to Mic Check Millionaire." 
        });
        
        // PART B: Show monetization modal immediately for reviewers
        // Do NOT navigate to any mock/legacy page
        setNewUser(data.user);
        setShowMonetizationModal(true);
      } else if (response.status === 403) {
        // PART B: Artist signup blocked - redirect to waitlist with friendly message
        toast({
          title: "Artist Access is Private Beta",
          description: "Join the waitlist to get early access!",
          variant: "default"
        });
        // Redirect to artist waitlist page
        setTimeout(() => {
          navigate('/artist-coming-soon');
        }, 1500);
      } else {
        toast({
          title: "Signup failed",
          description: data.detail || "Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast({
        title: "Error",
        description: "Failed to create account. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleGoogleSignup = () => {
    toast({ title: "Google signup", description: "Redirecting to Google..." });
  };

  // PART B: Handle monetization plan selection completion
  const handlePlanSelected = (updatedUser) => {
    setShowMonetizationModal(false);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setNewUser(updatedUser);
    
    // Check if user already has Stripe connected (unlikely for new signup, but possible)
    const hasStripe = updatedUser?.stripe_account_id;
    
    if (hasStripe) {
      // Already has Stripe, go directly to dashboard
      toast({
        title: "Setup complete!",
        description: "Redirecting to your dashboard..."
      });
      setTimeout(() => {
        navigate('/reviewer-dashboard');
      }, 500);
    } else {
      // Show Stripe setup wizard
      setShowStripeWizard(true);
    }
  };

  // Handle Stripe wizard skip - go to dashboard
  const handleStripeSkip = () => {
    setShowStripeWizard(false);
    toast({
      title: "Almost there!",
      description: "You can set up Stripe anytime in your dashboard settings."
    });
    setTimeout(() => {
      navigate('/reviewer-dashboard');
    }, 500);
  };

  // Handle Stripe wizard complete (user clicked connect - they'll be redirected to Stripe)
  const handleStripeComplete = () => {
    // User is being redirected to Stripe, no need to do anything here
    // They'll return to /stripe-complete after finishing
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* PART B: Monetization Modal - shows immediately after signup */}
      {showMonetizationModal && newUser && (
        <MonetizationPlanModal 
          user={newUser} 
          onPlanSelected={handlePlanSelected}
        />
      )}

      {/* Stripe Setup Wizard - shows after monetization plan selection */}
      {showStripeWizard && (
        <StripeSetupWizardModal
          isOpen={showStripeWizard}
          onClose={handleStripeSkip}
          onComplete={handleStripeComplete}
          onSkip={handleStripeSkip}
        />
      )}
      
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 group">
            <div className="bg-black p-3 rounded-lg group-hover:bg-[#F2D857] transition-colors">
              <Mic2 className="h-8 w-8 text-[#F2D857] group-hover:text-black" />
            </div>
          </Link>
          <h2 className="mt-6 text-3xl font-bold font-montserrat">
            Become a Reviewer
          </h2>
          <p className="mt-2 text-gray-600">
            Host review sessions and earn from your expertise
          </p>
          <p className="mt-2 text-sm font-medium text-green-600">
            ✓ Free to sign up
          </p>
        </div>

        <Card className="p-8">
          <form onSubmit={handleSignup} className="space-y-5">
            {/* PART A: Artist signup removed - Reviewer only notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold">Artists do not need an account!</p>
                <p className="mt-1">
                  Submit your music directly from the{' '}
                  <Link to="/upload" className="text-blue-600 underline hover:text-blue-800">Upload page</Link>
                  {' '}or join the{' '}
                  <Link to="/artist-coming-soon" className="text-blue-600 underline hover:text-blue-800">Artist Waitlist</Link>.
                </p>
              </div>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="name">Full Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            {/* Username */}
            <div>
              <Label htmlFor="username">Username</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="username"
                  type="text"
                  placeholder="johnjacxb"
                  value={formData.username}
                  onChange={handleUsernameChange}
                  className={`pl-10 h-11 ${
                    usernameStatus.available === false ? 'border-red-500' : 
                    usernameStatus.available === true ? 'border-green-500' : ''
                  }`}
                  required
                  minLength={3}
                />
                {usernameStatus.checking && (
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                    Checking...
                  </span>
                )}
                {usernameStatus.available === true && (
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-green-600">
                    ✓ Available
                  </span>
                )}
                {usernameStatus.available === false && (
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-red-600">
                    ✗ Taken
                  </span>
                )}
              </div>
              {usernameStatus.suggestions.length > 0 && (
                <p className="mt-1 text-xs text-gray-600">
                  Try: {usernameStatus.suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, username: s });
                        checkUsername(s);
                      }}
                      className="text-[#F2D857] hover:underline mr-2"
                    >
                      {s}
                    </button>
                  ))}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Lowercase letters, numbers, and underscores only
              </p>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-12 h-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-0 h-full w-12 flex items-center justify-center text-gray-400 hover:text-gray-600 active:text-gray-800 transition touch-manipulation"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="pl-10 pr-12 h-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-0 top-0 h-full w-12 flex items-center justify-center text-gray-400 hover:text-gray-600 active:text-gray-800 transition touch-manipulation"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full bg-black hover:bg-[#F2D857] hover:text-black text-white h-11 mt-6">
              Create Account
            </Button>
          </form>

          {/* Social Signup */}
          <div className="mt-6">
            <Separator className="my-6" />
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignup}
              className="w-full h-11 border-2"
            >
              <Chrome className="h-5 w-5 mr-2" />
              Sign up with Google
            </Button>
          </div>

          {/* Terms */}
          <p className="mt-4 text-xs text-center text-gray-500">
            By signing up, you agree to our{' '}
            <Link to="/terms" className="text-[#F2D857] hover:underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-[#F2D857] hover:underline">
              Privacy Policy
            </Link>
          </p>
        </Card>

        {/* Login Link */}
        <p className="mt-6 text-center text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-[#F2D857] font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;